import {useEffect, useMemo, useRef, useState, type ReactNode} from 'react'
import {Link} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import type {ApexOptions} from 'apexcharts'
import {ApexChart, useChartTokens, useSegmentNavigation} from '@/components/ui/apex-chart'
import {Button} from '@/components/ui/button'
import {cn} from '@/lib/utils'

const COUNTUP_MS = 700

/** El valor final siempre se escribe al terminar: una cifra cortada a medio camino sería falsa. */
export function CountUp({value, decimals = 0, suffix = ''}: {value: number; decimals?: number; suffix?: string}) {
  const [shown, setShown] = useState(value)
  const previous = useRef(value)
  useEffect(() => {
    const desde = previous.current
    previous.current = value
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || desde === value) {
      setShown(value)
      return
    }
    const inicio = performance.now()
    let frame = 0
    const paso = (ahora: number) => {
      const avance = Math.min(1, (ahora - inicio) / COUNTUP_MS)
      setShown(desde + (value - desde) * (1 - Math.pow(1 - avance, 3)))
      if (avance < 1) frame = requestAnimationFrame(paso)
      else setShown(value)
    }
    frame = requestAnimationFrame(paso)
    return () => cancelAnimationFrame(frame)
  }, [value])
  return (
    <>
      {shown.toLocaleString('es-CO', {minimumFractionDigits: decimals, maximumFractionDigits: decimals})}
      {suffix}
    </>
  )
}

export function Eyebrow({children, className}: {children: ReactNode; className?: string}) {
  return (
    <p
      className={cn(
        'font-mono text-[10px] font-semibold tracking-[0.12em] text-muted-foreground uppercase',
        className
      )}>
      {children}
    </p>
  )
}

export function TileCard({
  eyebrow,
  children,
  cta,
  testId,
  className,
}: {
  eyebrow: string
  children: ReactNode
  cta?: {label: string; to: string; testId?: string}
  testId: string
  className?: string
}) {
  return (
    <div className={cn('surface flex flex-col p-5', className)} data-testid={testId}>
      <div className="flex items-start justify-between gap-3">
        <Eyebrow>{eyebrow}</Eyebrow>
        {cta && (
          <Link
            to={cta.to}
            className="text-[12px] font-semibold whitespace-nowrap text-primary hover:underline"
            data-testid={cta.testId}>
            {cta.label} →
          </Link>
        )}
      </div>
      <div className="mt-3 flex-1">{children}</div>
    </div>
  )
}

/** Una gráfica en cero miente: en su lugar va lo que falta y el botón que lo empieza (T4). */
export function TileVacio({titulo, cta, testId}: {titulo: string; cta: {label: string; to: string}; testId: string}) {
  return (
    <div className="flex h-full flex-col" data-testid={testId}>
      <p className="text-sm font-semibold text-foreground">{titulo}</p>
      <Button asChild size="sm" variant="outline" className="mt-3.5 w-fit">
        <Link to={cta.to}>{cta.label}</Link>
      </Button>
    </div>
  )
}

export function DonutChart({
  labels,
  values,
  colors,
  totalLabel,
  onPick,
  testId,
  height = 210,
}: {
  labels: string[]
  values: number[]
  colors: string[]
  totalLabel: string
  onPick?: (index: number) => void
  testId: string
  height?: number
}) {
  const indices = useMemo(() => values.map((_, i) => i), [values])
  const dataPointSelection = useSegmentNavigation(indices, (index) => onPick?.(index))
  const total = values.reduce((acc, v) => acc + v, 0)
  const options = useMemo<ApexOptions>(
    () => ({
      // Sin la llave `events` si no navega: con `events: undefined` ApexCharts pisa sus defaults y
      // la dona no se dibuja.
      chart: onPick ? {events: {dataPointSelection}} : {},
      labels,
      colors,
      stroke: {width: 2, colors: ['transparent']},
      plotOptions: {
        pie: {
          donut: {
            size: '68%',
            labels: {
              show: true,
              value: {fontSize: '22px', fontWeight: 600, offsetY: 2},
              total: {show: true, label: totalLabel, fontSize: '10px', formatter: () => String(total)},
            },
          },
        },
      },
      tooltip: {y: {formatter: (value: number) => String(value)}},
    }),
    [labels, colors, totalLabel, total, onPick, dataPointSelection]
  )
  return (
    <ApexChart type="donut" height={height} options={options} series={values} clickable={!!onPick} testId={testId} />
  )
}

export function RadialGauge({
  value,
  label,
  color,
  testId,
  height = 220,
}: {
  value: number
  label: string
  color: string
  testId: string
  height?: number
}) {
  const tokens = useChartTokens()
  const options = useMemo<ApexOptions>(
    () => ({
      colors: [color],
      labels: [label],
      plotOptions: {
        radialBar: {
          hollow: {size: '62%'},
          track: {background: tokens.track, strokeWidth: '100%'},
          dataLabels: {
            name: {fontSize: '10px', color: tokens['muted-foreground'], offsetY: 20},
            value: {fontSize: '30px', fontWeight: 600, color: tokens.foreground, offsetY: -14},
          },
        },
      },
      stroke: {lineCap: 'round'},
    }),
    [color, label, tokens]
  )
  return <ApexChart type="radialBar" height={height} options={options} series={[value]} testId={testId} />
}

export type FilaCola = {
  clave: string
  severidad: 'red' | 'amber' | 'lime'
  que: string
  detalle?: string | null
  quien?: string | null
  cuando?: string | null
  cta: string
  to: string
}

const SEVERIDAD_PUNTO: Record<FilaCola['severidad'], string> = {
  red: 'bg-tone-red-fg',
  amber: 'bg-tone-amber-fg',
  lime: 'bg-tone-lime-fg',
}

/** `estrecha`: en media pantalla «de quién» y «cuándo» bajan bajo el qué; como columnas con
 * `whitespace-nowrap` partían la primera en un renglón por palabra. */
export function ColaTabla({filas, testId, estrecha = false}: {filas: FilaCola[]; testId: string; estrecha?: boolean}) {
  const {t} = useTranslation()
  return (
    <div className="surface overflow-hidden" data-testid={testId}>
      <table className="w-full text-left text-sm">
        <tbody>
          {filas.map((fila) => (
            <tr
              key={fila.clave}
              className="surface-divider border-b transition-colors last:border-b-0 hover:bg-muted/45"
              data-testid={`cola-${fila.clave}`}>
              <td className="py-3 pr-2 pl-5 align-middle">
                {/* El punto es la única marca de urgencia: su nombre va en `sr-only` (U17). */}
                <span
                  className={cn('inline-block h-2.5 w-2.5 shrink-0 rounded-full', SEVERIDAD_PUNTO[fila.severidad])}
                />
                <span className="sr-only">{t(`home.cola.severidad.${fila.severidad}`)}</span>
              </td>
              <td className="py-3 pr-3 align-middle">
                <span className="text-foreground">{fila.que}</span>
                {fila.detalle && <span className="block text-[12.5px] text-muted-foreground">{fila.detalle}</span>}
                {(fila.quien || fila.cuando) && (
                  <span
                    className={cn(
                      'mt-0.5 block font-mono text-[11.5px] text-muted-foreground',
                      !estrecha && 'lg:hidden'
                    )}>
                    {[fila.quien, fila.cuando].filter(Boolean).join(' · ')}
                  </span>
                )}
              </td>
              {!estrecha && (
                <>
                  <td className="hidden py-3 pr-3 align-middle text-[12.5px] text-muted-foreground lg:table-cell">
                    {fila.quien}
                  </td>
                  <td className="hidden py-3 pr-3 text-right align-middle font-mono text-xs whitespace-nowrap text-muted-foreground lg:table-cell">
                    {fila.cuando}
                  </td>
                </>
              )}
              <td className="py-3 pr-5 text-right align-middle">
                <Link to={fila.to} className="text-[13px] font-semibold whitespace-nowrap text-primary hover:underline">
                  {fila.cta}
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
